import { motion } from 'framer-motion'
import { 
  Users, 
  Award, 
  Target, 
  Clock,
  BookOpen,
  Headphones,
  Briefcase,
  BadgeCheck
} from 'lucide-react'

const WhyChooseUs = () => {
  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Industry Mentors',
      description: 'Learn from experienced DevOps engineers working in top MNCs with real-world insights',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Real DevOps Projects',
      description: 'Work on 15+ hands-on projects that mirror actual industry challenges',
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: 'Live Labs Access',
      description: 'Get 24/7 access to cloud labs with pre-configured environments',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: 'Placement Support',
      description: 'Dedicated placement assistance with 500+ hiring partners',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: 'Career Guidance',
      description: '1-on-1 career counseling and interview preparation sessions',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100'
    },
    {
      icon: <BadgeCheck className="w-8 h-8" />,
      title: 'Certification Ready',
      description: 'Curriculum mapped to AWS, Azure, GCP and Kubernetes certification exams',
      color: 'text-pink-600',
      bgColor: 'bg-pink-100'
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  }

  return (
    <section className="section-padding bg-white dark:bg-gray-900">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Why Choose Deoriginlabs?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            We provide everything you need to succeed in your DevOps career journey
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group"
            >
              <div className="card card-hover p-8 text-center h-full dark:bg-gray-800 dark:border-gray-700">
                <div className={`w-20 h-20 ${feature.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <span className={feature.color}>{feature.icon}</span>
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-16 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-gray-800 rounded-2xl p-8 lg:p-12"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                More Than Just a Course
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                We don't just teach DevOps - we build careers. Our comprehensive program includes:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Award className="w-5 h-5 text-primary-600 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">Industry-recognized certifications</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-primary-600 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">Flexible learning schedules</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Users className="w-5 h-5 text-primary-600 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">Peer learning and collaboration</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Target className="w-5 h-5 text-primary-600 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300">Goal-oriented training approach</span>
                </li>
              </ul>
            </div>
            
            <div className="relative">
              <div className="aspect-video bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/30 dark:to-gray-700 rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-medium mx-auto mb-4">
                    <Award className="w-10 h-10 text-primary-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Success Guaranteed
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    Join 500+ successful professionals
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default WhyChooseUs
